// Button3D.tsx

import React, { type CSSProperties } from "react";
import "./Button3D.scss";
import { CircularLoader } from "../CircularLoadingIndicator/CircularLoadingIndicator";

interface Button3DProps {
    onClick: () => void;
    disabled?: boolean;
    loading?: {
        loadingSource: "pre-icon" | "post-icon" | "text" | "children";
    };
    className?: string;

    option?: {
        icon?: {
            // icon: React.ReactNode;
            icon: React.ReactElement<React.SVGProps<SVGSVGElement>>;
            color?: string;
            size?: string;
        };
        selected: boolean;
        onClick: (option: string) => void;
    };

    // EITHER TEXT OR CHILDREN
    text?: string;
    children?: React.ReactNode;

    // PRE-ICON
    preIconButton?: {
        icon: React.ReactNode;
        onClick: () => void;
    };

    // POST-ICON
    postIconButton?: {
        icon: React.ReactNode;
        onClick: () => void;
    };
}

export default function Button3D({
    onClick,
    disabled,
    loading,
    option,
    className,
    text,
    preIconButton,
    postIconButton,
    children,
}: Button3DProps) {
    // CAN'T HAVE BOTH TEXT AND CHILDREN
    if (text && typeof children !== "undefined") {
        throw new Error("Cannot have both text and children in Button3D");
    }
    // CAN'T HAVE NEITHER TEXT NOR CHILDREN
    if (!text && typeof children === "undefined") {
        throw new Error("Button3D must have either text or children");
    }
    // CAN'T HAVE BOTH PRE-ICON AND POST-ICON
    if (preIconButton && postIconButton) {
        throw new Error(
            "Button3D cannot have both preIconButton and postIconButton",
        );
    }

    const buttonType: "standalone" | "option" = option
        ? "option"
        : "standalone";

    return (
        <button
            className={`button-3d ${className || ""} ${
                loading !== undefined ? "loading" : ""
            } ${disabled ? "disabled" : ""} 
            ${buttonType}
            ${option?.selected ? "selected" : ""}
            ${buttonType === "option" && option?.icon ? "has-icon" : "no-icon"}
            `}
            disabled={disabled || loading !== undefined}
            aria-label={text}
            title={text}
            type="button"
            onClick={onClick}
        >
            <div
                className="button-3d__face"
                style={{
                    width: children ? "100%" : undefined,
                }}
            >
                {/* PRE-ICON-BUTTON */}
                {preIconButton &&
                    (loading === undefined ||
                    (loading !== undefined &&
                        loading.loadingSource === "pre-icon") ? (
                        <div
                            className={`button-3d__face__pre-icon ${
                                loading?.loadingSource === "pre-icon"
                                    ? "loading-source"
                                    : ""
                            }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                preIconButton.onClick();
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Pre-icon button"
                            title="Pre-icon button"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    preIconButton.onClick();
                                }
                            }}
                        >
                            {preIconButton.icon}
                        </div>
                    ) : null)}

                {/* OPTION-ICON */}
                {option?.icon && (
                    <div className="button-3d__face__option-icon">
                        {React.cloneElement(option.icon.icon, {
                            style: {
                                width: option.icon.size,
                                height: option.icon.size,
                                color: option.icon.color,
                                ...(option.icon.icon.props.style ?? {}),
                            } as CSSProperties,
                        })}
                    </div>
                )}

                {/* TEXT */}
                {text &&
                    (loading === undefined ||
                    (loading !== undefined &&
                        loading.loadingSource === "text") ? (
                        <span className="button-3d__face__text">{text}</span>
                    ) : null)}

                {/* CHILDREN */}
                {children &&
                    (loading === undefined ||
                    (loading !== undefined &&
                        loading.loadingSource === "children") ? (
                        <div className="button-3d__face__children">
                            {children}
                        </div>
                    ) : null)}

                {/* LOADING SPINNER */}
                {loading && (
                    <div className="button-3d__face__loading">
                        <CircularLoader color="#B1AFB2" />
                    </div>
                )}

                {/* POST-ICON-BUTTON */}
                {postIconButton &&
                    (loading === undefined ||
                    (loading !== undefined &&
                        loading.loadingSource === "post-icon") ? (
                        <div
                            className={`button-3d__face__post-icon ${
                                loading?.loadingSource === "post-icon"
                                    ? "loading-source"
                                    : ""
                            }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                postIconButton.onClick();
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Post-icon button"
                            title="Post-icon button"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    postIconButton.onClick();
                                }
                            }}
                        >
                            {postIconButton.icon}
                        </div>
                    ) : null)}
            </div>
        </button>
    );
}
